import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Box, Group, Drawer, Button, Select, Text, useMantineTheme, TextInput, ActionIcon } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { regexNumberOnly, regexStringAlphaNumOnlyUpperCase, regexDecimal, numberWithComma, isoDateToSqlDate } from "../../utils/globalFunctions";

import InputComponent from "../../components/general/InputComponent";
import InputWrapperCustom from "../../components/general/InputWrapperCustom";
import PickerDate from "../../components/general/PickerDate";

import ModalResponse from "../../components/general/ModalResponse";

const DiscountMasterAdd = ({
  stateShow,
  fnShow,
  fnSuccessAdd,
}) => {

  const mountedRef = React.useRef(true);
  const theme = useMantineTheme();
  const { loginContext, handleSetLoginContext } = useLoginContext();

  const notificationId = React.useRef({
    errorSubmitData: 'diskonMasterAdd-1',
    successSubmitData: 'diskonMasterAdd-2',
  })

  const [data, setData] = React.useState({
    submitLoading: false,

    diskonCodePrefix: '',
    diskonCodePrefixError: '',

    diskonQuota: '',
    diskonQuotaError: '',

    diskonNeedCode: '1',
    diskonNeedCodeError: '',
    diskonCodeLongDigit: '5',

    diskonValidDateStart: new Date(),
    diskonValidDateStartError: '',

    diskonValidDateTo: null,
    diskonValidDateToError: '',

    diskonDeductedFrom: utils.discountDeductedFrom.jasa.value,

    diskonPercentRupiahType: utils.discountType.percent.value,

    diskonPercentRupiahValue: '',
    diskonPercentRupiahError: '',

    modalResponse: {
      show: false,
      type: utils.modalResponse.warning,
      hideOnClickOutSide: false,
      content: ''
    }
  });

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    }
  }, [])

  React.useEffect(() => {
    setData(d => ({ ...d, diskonPercentRupiahValue: '' }))
  }, [data.diskonPercentRupiahType])

  const addData = () => {
    setData(d => ({
      ...d,
      diskonCodePrefixError: '',
      diskonQuotaError: '',
      diskonNeedCodeError: '',
      diskonPercentRupiahError: '',
      diskonValidDateStartError: '',
      diskonValidDateToError: ''
    }));

    if (validator.isEmpty(data.diskonCodePrefix, { ignore_whitespace: true })) {
      setData(d => ({ ...d, diskonCodePrefixError: 'Kolom wajib diisi.' }));
    } else if (!validator.isLength(data.diskonCodePrefix, { min: 3, max: 20 })) {
      setData(d => ({ ...d, diskonCodePrefixError: 'Minimal 3 digit dan maximal 20 digit.' }));
    } else if (validator.isEmpty(String(data.diskonQuota), { ignore_whitespace: true })) {
      setData(d => ({ ...d, diskonQuotaError: 'Kolom wajib diisi.' }));
    } else if (data.diskonNeedCode === null) {
      setData(d => ({ ...d, diskonNeedCodeError: 'Kolom wajib diisi.' }));
    } else if (validator.isEmpty(data.diskonPercentRupiahValue, { ignore_whitespace: true })) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Kolom wajib diisi.' }));
    } else if ((data.diskonPercentRupiahType === utils.discountType.percent.value) && (Number(data.diskonPercentRupiahValue) > 100)) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Maximal 100%' }));
    } else if (!validator.isDate(data.diskonValidDateStart)) {
      setData(d => ({ ...d, diskonValidDateStartError: 'Kolom wajib diisi!' }));
    } else if (!validator.isDate(data.diskonValidDateTo)) {
      setData(d => ({ ...d, diskonValidDateToError: 'Kolom wajib diisi!' }));
    } else if ((data.diskonDeductedFrom === utils.discountDeductedFrom.jasa.value) && (data.diskonPercentRupiahType === utils.discountType.rupiah.value)) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Kombinasi hanya bisa Jasa % dan Total Rp' }));
    } else if ((data.diskonDeductedFrom === utils.discountDeductedFrom.total.value) && (data.diskonPercentRupiahType === utils.discountType.percent.value)) {
      setData(d => ({ ...d, diskonPercentRupiahError: 'Kombinasi hanya bisa Jasa % dan Total Rp' }));
    } else {
      setData(d => ({ ...d, submitLoading: true }));

      const formDT = new FormData();
      const formDTHeader = { 'Content-Type': 'application/json' }

      formDT.append('json_token', loginContext.syswebapp);
      formDT.append('nama', data.diskonCodePrefix);
      formDT.append('quota', data.diskonQuota);
      formDT.append('need_code', data.diskonNeedCode);
      formDT.append('code_long_digit', data.diskonCodeLongDigit);
      formDT.append('deducted_from', data.diskonDeductedFrom);
      formDT.append('type', data.diskonPercentRupiahType);
      formDT.append('disc_val', data.diskonPercentRupiahValue);
      formDT.append('date_from', isoDateToSqlDate(data.diskonValidDateStart, ''));
      formDT.append('date_to', isoDateToSqlDate(data.diskonValidDateTo, ''));

      formDT.append('deducted_from_obj', JSON.stringify(utils.discountDeductedFrom));
      formDT.append('type_obj', JSON.stringify(utils.discountType));
      formDT.append('discount_flag', utils.discountFlag.voucher.value);

      axios({
        method: 'post',
        url: BE_API_ONE + '/discount_master_add',
        data: formDT,
        headers: formDTHeader,
      }).then((response) => {
        if (!mountedRef.current) return null;

        setData(d => ({ ...d, submitLoading: false }));

        if (response.data.flag_status === 'isset') {
          fnSuccessAdd();

          setData(d => ({
            ...d,
            diskonCodePrefix: '',
            diskonQuota: '',
            diskonNeedCode: '1',
            diskonCodeLongDigit: '5',
            diskonValidDateStart: new Date(),
            diskonValidDateTo: null,
            diskonDeductedFrom: utils.discountDeductedFrom.jasa.value,
            diskonPercentRupiahType: utils.discountType.percent.value,
            diskonPercentRupiahValue: '',
          }));

          notifications.show({
            id: notificationId.current.successSubmitData,
            title: utils.notification.success.caption,
            message: 'Berhasil Tambah Data.',
            color: utils.notification.success.color,
            icon: utils.notification.success.icon,
            withBorder: true,
            autoClose: true,
          })
        }

        if (response.data.flag_status === 'error') {
          notifications.show({
            id: notificationId.current.errorSubmitData,
            title: utils.notification.warning.caption,
            message: response.data.pesan,
            color: utils.notification.warning.color,
            icon: utils.notification.warning.icon,
            withBorder: true,
            autoClose: true,
          })
        }

        if (response.data.flag_status === 'duplicate-name') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: false,
              content: `Nama diskon "${response.data.suspect}" sudah pernah digunakan.`,
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'duplicate-code') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: false,
              content: `Terjadi kesalahan. Mohon ulangi proses.`,
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'invalid-json-token') {
          handleSetLoginContext(null);
        }

        if (response.data.flag_status === 'user-inactive') {
          handleSetLoginContext(null);
        }
      }).catch(error => {
        if (!mountedRef.current) return null;

        setData(d => ({ ...d, submitLoading: false }));

        notifications.show({
          id: notificationId.current.errorSubmitData,
          title: utils.notification.warning.caption,
          message: error.message,
          color: utils.notification.warning.color,
          icon: utils.notification.warning.icon,
          withBorder: true,
          autoClose: false,
        })
      })
    }
  }

  return (
    <React.Fragment>
      <Drawer
        opened={stateShow}
        onClose={fnShow}
        withCloseButton={false}
        position="left"
        closeOnClickOutside={true}

        title={
          <Group noWrap spacing='xs'>
            <ActionIcon
              size='lg'
              variant="light"
              radius='xl'
              color="blue"
              onClick={fnShow}
            >
              <IconArrowLeft size='1.25rem' stroke={1.5} />
            </ActionIcon>

            <Text>Tambah Data Voucher</Text>
          </Group>
        }
        size='md'

        styles={() => ({
          title: {
            fontWeight: 'bold',
            textTransform: 'uppercase'
          }
        })}
      >
        <Box pt='xs' pb='md'>
          <Box mb='1rem'>
            <InputComponent
              stateId='diskonMasterAddCodePrefix'
              stateWithAsterisk={true}
              stateLabel='Nama Voucher'
              stateDescription='Nama 3-20 digit. Contoh: NATAL25'
              statePlaceHolder='Nama Voucher'
              stateValue={data.diskonCodePrefix}
              stateError={data.diskonCodePrefixError}
              stateStylesInput={{ marginTop: '.75rem' }}
              fnOnChange={(e) => setData(d => ({ ...d, diskonCodePrefix: regexStringAlphaNumOnlyUpperCase(e.target.value) }))}
            />
          </Box>

          <Box mb='1rem'>
            <InputComponent
              stateId='diskonMasterAddQuota'
              stateWithAsterisk={true}
              stateLabel='Quota'
              stateDescription=''
              statePlaceHolder='Quota Voucher'
              stateValue={data.diskonQuota}
              stateError={data.diskonQuotaError}
              stateStylesInput={{ marginTop: '.25rem' }}
              fnOnChange={(e) => setData(d => ({ ...d, diskonQuota: regexNumberOnly(e.target.value) }))}
            />
          </Box>

          <Select
            mt='.5rem'
            withAsterisk={true}
            label='Kode Voucher'
            placeholder="Panjang Digit"
            description='Kode akan digenerate random sebanyak digit tertentu sesuai pilihan.'

            data={[
              { value: '5', label: '5 Digit' },
              { value: '6', label: '6 Digit' },
              { value: '7', label: '7 Digit' },
            ]}
            value={data.diskonCodeLongDigit}
            onChange={(val) => setData(d => ({ ...d, diskonCodeLongDigit: val }))}

            styles={() => ({
              label: { marginBottom: '.25rem' }
            })}
          />

          <InputWrapperCustom
            stateMT="1rem"
            stateLabel='Pemotongan Voucher'
            stateWithAsterisk={true}
            stateError=''
            stateDescription=''
          >
            <Select
              value={data.diskonDeductedFrom}
              placeholder="Voucher dipotong dari"
              description=''
              onChange={(val) => setData(d => ({ ...d, diskonDeductedFrom: val }))}
              data={[
                // { value: utils.discountDeductedFrom.item.value, label: utils.discountDeductedFrom.item.label },
                { value: utils.discountDeductedFrom.jasa.value, label: utils.discountDeductedFrom.jasa.label },
                { value: utils.discountDeductedFrom.total.value, label: utils.discountDeductedFrom.total.label }
              ]}
            />
          </InputWrapperCustom>

          <Group mt='1rem' noWrap spacing='xs' position='apart'>
            <Select
              value={data.diskonPercentRupiahType}
              label="Tipe Voucher"
              placeholder="Tipe Voucher"
              withAsterisk={true}
              description=''
              onChange={(val) => setData(d => ({ ...d, diskonPercentRupiahType: val }))}
              data={[
                { value: utils.discountType.percent.value, label: utils.discountType.percent.label },
                { value: utils.discountType.rupiah.value, label: utils.discountType.rupiah.label },
              ]}
            />

            <TextInput
              id='diskonPercentRupiahValue'
              size="sm"
              withAsterisk={true}
              variant='default'
              label='Besar Voucher'
              placeholder='Besar Voucher'
              readOnly={false}
              disabled={false}
              icon={data.diskonPercentRupiahType === utils.discountType.rupiah.value ? <Text size='sm'>Rp</Text> : null}
              rightSection={data.diskonPercentRupiahType === utils.discountType.percent.value ? <Text size='sm' color='dimmed'>%</Text> : null}

              value={data.diskonPercentRupiahType === utils.discountType.percent.value ? data.diskonPercentRupiahValue : numberWithComma(data.diskonPercentRupiahValue)}

              onChange={(e) => setData(d => ({
                ...d,
                diskonPercentRupiahValue: d.diskonPercentRupiahType === utils.discountType.percent.value ? regexDecimal(e.target.value) : regexNumberOnly(e.target.value)
              }))}
            />
          </Group>

          {data.diskonPercentRupiahError &&
            <Text
              c={theme.colorScheme === 'light' ? theme.colors.red[6] : theme.colors.red[2]}
              size={theme.fontSizes.xs}
              mt='.25rem'
              mb='.5rem'
            >
              {data.diskonPercentRupiahError}
            </Text>
          }

          <Group mt='1rem' position='apart' noWrap grow spacing='xs'>
            <PickerDate
              stateDate={data.diskonValidDateStart}
              stateVariant="default"
              stateClearable={false}
              stateMinDate={null}
              fnSetDate={(val) => setData(d => ({ ...d, diskonValidDateStart: val }))}

              stateFormat='DD MMM YYYY'
              stateLabel='Valid dari'
              statePlaceHolder='Valid dari'
              stateWithAsterisk={true}
              stateDescription=''
              stateError=''
            />

            <PickerDate
              stateDate={data.diskonValidDateTo}
              stateVariant="default"
              stateClearable={false}
              stateMinDate={data.diskonValidDateStart}
              fnSetDate={(val) => setData(d => ({ ...d, diskonValidDateTo: val }))}

              stateFormat='DD MMM YYYY'
              stateLabel='Valid sampai'
              statePlaceHolder='Valid sampai'
              stateWithAsterisk={true}
              stateDescription=''
              stateError=''
            />
          </Group>

          {(data.diskonValidDateStartError || data.diskonValidDateToError) &&
            <Box
              mt='.25rem'
              mb='.5rem'
            >
              <Text
                c={theme.colorScheme === 'light' ? theme.colors.red[6] : theme.colors.red[2]}
                size={theme.fontSizes.xs}
              >
                {data.diskonValidDateStartError}
              </Text>

              <Text
                c={theme.colorScheme === 'light' ? theme.colors.red[6] : theme.colors.red[2]}
                size={theme.fontSizes.xs}
              >
                {data.diskonValidDateToError}
              </Text>
            </Box>
          }

          <Group position='center' noWrap my='xl'>
            <Button
              fullWidth
              loading={data.submitLoading}
              onClick={addData}
            >
              Submit
            </Button>
          </Group>
        </Box>
      </Drawer>

      <ModalResponse
        stateShow={data.modalResponse.show}
        fnHide={() => setData(d => ({
          ...d,
          modalResponse: {
            ...d.modalResponse,
            show: false
          }
        }))}

        stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
        stateType={data.modalResponse.type}
        stateContent={data.modalResponse.content}
      />

    </React.Fragment>
  )
}

export default DiscountMasterAdd;